import {
  Component,
  DestroyRef,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { FormGroup } from '@angular/forms';
import { RestApiService } from '@core/services/rest-api.service';
import { DvDynamicFormElement, DvFormUtils, FormOf } from '@devinco/ui/legacy';
import { Entity } from '@shared/constants/entity';
import { AddressLookupResp } from '@shared/interfaces/address-lookup';
import { CustomFleetMapSettings, FleetDto } from '@pages/fleet/interfaces';
import { FleetGeofenceService } from '@pages/fleet/services/fleet-geofence.service';
import { FleetFilteredItemsService } from '@pages/fleet/services/fleet-filtered-items.service';
import {
  AutoZoomFitParams,
  Coordinates,
  MapInput,
  MapShape,
} from '@features/map/interfaces';
import {
  BehaviorSubject,
  filter,
  interval,
  map,
  merge,
  Observable,
  of,
  skip,
  Subject,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { FleetGridService } from '../fleet-grid/fleet-grid.service';
import { FleetMapService } from './fleet-map.service';
import { AddressSearchService } from '@features/map/services/address-search.service';
import { FleetLocationDto } from '@pages/settings-page/fleet-settings/interfaces';
import { MapModule } from '@features/map/map.module';
import { TimedMarkerMapMenuPanelComponent } from '@shared/components/timed-marker-map-menu-panel/timed-marker-map-menu-panel.component';
import { AddressSearchResultBoxComponent } from '@shared/components/address-search-result-box/address-search-result-box.component';
import { TranslateModule } from '@ngx-translate/core';
import { AsyncPipe, DatePipe, NgClass } from '@angular/common';
import { MapSetting, MapShapeType } from '@features/map/constants/constants';
import { HistoricalPositionToggleComponent } from '@pages/fleet/components/historical-position-toggle/historical-position-toggle.component';
import { MapId } from '@features/map/constants/map-id.enum';
import { HistoricalPositionService } from '@pages/fleet/services/historical-position-service';
import { MapComponent } from '@features/map/map.component';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { DvAlertBannerComponent } from '@devinco/ui/dds/alert-banner';

@Component({
  imports: [
    MapModule,
    TimedMarkerMapMenuPanelComponent,
    AddressSearchResultBoxComponent,
    TranslateModule,
    AsyncPipe,
    HistoricalPositionToggleComponent,
    DvAlertBannerComponent,
    DatePipe,
    NgClass,
  ],
  selector: 'app-fleet-map',
  templateUrl: './fleet-map.component.html',
  styleUrls: ['./fleet-map.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [FleetGridService, FleetMapService],
})
export class FleetMapComponent implements OnInit, OnDestroy {
  historicalPositionService = inject(HistoricalPositionService);
  readonly splitterEvents = inject(DvSplitterEventsService);
  private fleetItemSelectionService = inject(FleetFilteredItemsService);
  private api = inject(RestApiService);
  private fleetGridService = inject(FleetGridService);
  private fleetGeofenceService = inject(FleetGeofenceService);
  private mapService = inject(FleetMapService);
  private addressSearchService = inject(AddressSearchService);
  private injector = inject(Injector);

  markers = signal<MapInput[]>([]);
  showGeofenceLegend = signal<boolean>(false);
  refreshSubscription: Subscription | undefined;
  autoZoomFit$ = new BehaviorSubject<AutoZoomFitParams>({
    doAutoFit: false,
  });
  toggleMarkerClustering$ = new BehaviorSubject<boolean>(false);
  searchResult$: Observable<AddressLookupResp[]>;
  geofences$: BehaviorSubject<MapShape[] | undefined>;
  deleteGeofenceTrigger$: Subject<number | string>;
  shapeRightClickTrigger$ = new Subject<(number | string)[]>();
  customSettingsForm: FormGroup<FormOf<CustomFleetMapSettings>>;
  customSettingsElements: DvDynamicFormElement[];

  private map = viewChild.required<MapComponent | undefined>(MapComponent);

  readonly mapSetting = MapSetting.FleetMap;
  readonly mapId = MapId.FleetMap;

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.geofences$ = this.fleetGeofenceService.geofences$;
    this.deleteGeofenceTrigger$ =
      this.fleetGeofenceService.deleteGeofenceTrigger$;
    this.customSettingsElements = this.mapService.customFleetSettingElements;
    this.customSettingsForm = DvFormUtils.createFormGroup(
      this.customSettingsElements
    );
    this.setupEvents();
  }

  ngOnDestroy(): void {
    this.fleetGeofenceService.geofences$.next([]);
  }

  bannerClickFn = (): void => {
    this.historicalPositionService.selectedHistoricalDateTime.update(
      () => undefined
    );
  };

  onAutoRefreshClicked(isAutoRefreshOn: boolean): void {
    if (isAutoRefreshOn) {
      // fleet map needs to be updated more frequently
      this.refreshSubscription = interval(15000)
        .pipe(
          switchMap(() => {
            return this.historicalPositionService.selectedHistoricalDateTime()
              ? of(undefined)
              : this.getFleet().pipe(
                  tap(resp => {
                    const currentFilteredRows =
                      this.fleetItemSelectionService.fleetGridFilterChanged$.getValue();
                    const selectedMarkers = resp.filter(
                      row =>
                        !!currentFilteredRows.find(
                          filteredRow => filteredRow.FleetID === row.FleetID
                        )
                    );
                    this.createMapMarkers(selectedMarkers);
                  }),
                  switchMap(resp =>
                    this.hasVisibleGeofences()
                      ? this.fetchGeofenceShapes(resp).pipe(
                          tap(shapes => this.geofences$.next(shapes))
                        )
                      : of(undefined)
                  )
                );
          }),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
    } else {
      this.refreshSubscription?.unsubscribe();
    }
  }

  onAutoZoomFitClicked(isAutoZoomOn: boolean): void {
    this.autoZoomFit$.next({ doAutoFit: isAutoZoomOn });
  }

  onGroupMarkersClicked(isClusteringOn: boolean): void {
    this.toggleMarkerClustering$.next(isClusteringOn);
  }

  onSearchAddressChanged(searchTerm: string | undefined): void {
    this.searchResult$ = searchTerm
      ? this.addressSearchService.getGPSPositionFromAddress({
          Address: searchTerm,
        })
      : of([]);
  }

  onAddressSelected(address: AddressLookupResp): void {
    if (!address.Position) return;
    this.autoZoomFit$.next({
      doAutoFit: true,
      coordinates: {
        lat: address.Position.Latitude,
        lng: address.Position.Longitude,
      },
    });
  }

  onGeofenceToggleClicked(isShown: boolean): void {
    this.showGeofenceLegend.set(isShown);
    if (isShown) {
      this.fetchGeofenceShapes(
        this.fleetItemSelectionService.fleetGridFilterChanged$.getValue()
      )
        .pipe(take(1))
        .subscribe(shapes => this.geofences$.next(shapes));
    } else {
      this.geofences$.next([]);
    }
  }

  onCustomSettingsChanged(settings: CustomFleetMapSettings): void {
    this.createMapMarkers(
      this.fleetItemSelectionService.fleetGridFilterChanged$.getValue(),
      settings
    );
  }

  private hasVisibleGeofences(): boolean {
    return this.showGeofenceLegend() && !!this.geofences$.getValue()?.length;
  }

  private fetchGeofenceShapes(fleet: FleetDto[]): Observable<MapShape[]> {
    return this.mapService.fetchGeofences().pipe(
      map(resp => {
        const vehiclePositions = this.getVehiclePositionsById(fleet);
        return resp.map((item: FleetLocationDto) => {
          const linkedVehicleId = item.IsTemporary
            ? item.TemporaryFleetLocationTrackerObjectID
            : undefined;
          return {
            type: MapShapeType.Circle,
            ...JSON.parse(item.GeoJson),
            color: item.IsTemporary ? 'blue' : 'orange',
            id: item.FleetLocationID,
            description: item.Description,
            entityId: item.TemporaryFleetLocationTrackerObjectID,
            linkedVehicleId,
            linkedPosition:
              linkedVehicleId !== undefined
                ? vehiclePositions.get(linkedVehicleId)
                : undefined,
          } satisfies MapShape;
        });
      })
    );
  }

  private getFleet(): Observable<FleetDto[]> {
    return this.api
      .getData({
        entity: Entity.Fleet,
        fields: this.fleetGridService.colDefs
          .map(colDef => colDef.field!)
          .filter(colDef => !!colDef),
      })
      .pipe(map(resp => resp.data));
  }

  private subscribeToShapeRightClickTrigger(): void {
    this.shapeRightClickTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(shapeIds => {
        this.fleetGeofenceService.openDeleteFleetLocationDialog(
          shapeIds.map(id => Number(id))
        );
      });
  }

  private setupEvents(): void {
    this.subscribeToFleetFilterChanged();
    this.subscribeToShapeRightClickTrigger();
    this.subscribeToHistoricalPositionTriggers();
  }

  private createMapMarkers(
    fleet: FleetDto[],
    settings?: CustomFleetMapSettings
  ): void {
    this.markers.set(
      this.mapService.createMapMarkers(
        fleet,
        settings ?? this.customSettingsForm.value
      )
    );
    this.map()?.updateGeofenceConnectorPositions(
      this.getVehiclePositionsById(fleet)
    );
  }

  private getVehiclePositionsById(fleet: FleetDto[]): Map<number, Coordinates> {
    const positions = new Map<number, Coordinates>();
    fleet.forEach(item => {
      if (
        item.VehicleID !== undefined &&
        item.LastGPSLat !== undefined &&
        item.LastGPSLon !== undefined
      ) {
        positions.set(item.VehicleID, {
          lat: item.LastGPSLat,
          lng: item.LastGPSLon,
        });
      }
    });
    return positions;
  }

  private subscribeToHistoricalPositionTriggers(): void {
    merge(
      toObservable(this.historicalPositionService.selectedHistoricalDateTime, {
        injector: this.injector,
      }),
      this.historicalPositionService.historicalPositionFetchTrigger$
    )
      .pipe(skip(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(time => {
        if (time) {
          this.historicalPositionService
            .fetchHistoricalFleetPositions(
              time,
              this.fleetItemSelectionService.fleetGridFilterChanged$
                .getValue()
                .map(fleet => fleet.FleetID!)
            )
            .pipe(take(1))
            .subscribe(resp => {
              this.createMapMarkers(resp);
              this.map()?.toggleGrayOverlay('on');
            });
        } else {
          this.createMapMarkers(
            this.fleetItemSelectionService.fleetGridFilterChanged$.getValue()
          );
          this.map()?.toggleGrayOverlay('off');
        }
      });
  }

  private subscribeToFleetFilterChanged(): void {
    this.fleetItemSelectionService.fleetGridFilterChanged$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          () => !this.historicalPositionService.selectedHistoricalDateTime()
        )
      )
      .subscribe(selectedFleetItems => {
        const isAutoFitOff = !this.autoZoomFit$.getValue().doAutoFit;
        if (isAutoFitOff) {
          this.autoZoomFit$.next({ doAutoFit: true });
        }
        this.createMapMarkers(selectedFleetItems);

        if (isAutoFitOff) {
          // wait until marker change will be detected by fleet map
          setTimeout(() => {
            this.autoZoomFit$.next({ doAutoFit: false });
          });
        }
      });
  }
}
